import React, { useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import axiosInstance from "../../../services/AxiosInstance";
import { enhancedAccreditationService } from "../../../services/enhancedAccreditationApiService";
import ImageUploadWithCrop from "../../../components/ImageUploadWithCrop";
import SearchableUserTypeDropdown from "../../../admin/admincomponent/permission/SearchableUserTypeDropdown";
import ImageModal from "../../../components/ImageModal";
import GroupCityVenues from "../../../admin/admincomponent/users/GroupCityVenues";

// Helper to ensure URL has / before uploads and resolves to backendBase
const fixUploadUrl = (url) => {
  if (typeof url !== 'string' || !url) return '';
  const backendBase = (process.env.REACT_APP_BASE_URL || '').replace(/\/+$/, '');
  if (url.includes('.s3.') || url.includes('amazonaws.com')) {
    return url;
  }
  if (url.includes('/uploads/')) {
    const uploadPath = url.substring(url.indexOf('/uploads/'));
    return backendBase ? `${backendBase}${uploadPath}` : uploadPath;
  }
  return url.replace(/([a-zA-Z0-9])uploads\//, '$1/uploads/');
};

// Lightweight edit modal that mirrors ManualForm visual rhythm but scoped to a modal
const EditAccreditationModal = ({
  show,
  onClose,
  initialData, // contains existing registration data (ids, text fields, image urls)
  onUpdated, // callback with updated record
}) => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({});
  const [originalForm, setOriginalForm] = useState({});
  const [subtypes, setSubTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [existingPhotoUrl, setExistingPhotoUrl] = useState("");
  const [existingOfficeIdUrl, setExistingOfficeIdUrl] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedImageAlt, setSelectedImageAlt] = useState("");
  const [imageURL, setImageURL] = useState(null);
  const [officeIDImageURL, setOfficeIDImageURL] = useState(null);
  const [fileError, setFileError] = useState("");
  const [officeIDError, setOfficeIDError] = useState("");
  const [uploadLetterError, setUploadLetterError] = useState("");
  const [infinitySelected, setInfinitySelected] = useState(false);
  const [allCitiesAndVenues, setAllCitiesAndVenues] = useState({});
  const [selectedVenueIds, setSelectedVenueIds] = useState([]);
  const [selectedVenueIdsState, setSelectedVenueIdsState] = useState([]);
  const [uploadLetterUploaded, setUploadLetterUploaded] = useState(false);
  const [userType, setUserType] = useState("");
  const [showHiddenFields, setShowHiddenFields] = useState(false);
  const [isDesignationDropdown, setIsDesignationDropdown] = useState(false);
  const [isMedia, setIsMedia] = useState(false);
  const [previousNonMediaDesignation, setPreviousNonMediaDesignation] = useState("");

  const uploadLetterInputRef = useRef(null);

  useEffect(() => {
    if (show && initialData) {
      // Map API response structure to form fields
      const mapped = {
        registration_data_id: initialData?.registration_data_id || initialData?.id,
        id: initialData?.registration_data_id || initialData?.id,
        firstname: initialData?.first_name || "",
        middlename: initialData?.middle_name || "",
        lastname: initialData?.last_name || "",
        email: initialData?.email_id || "",
        mobile: initialData?.mobile_number || "",
        mobile_number: initialData?.mobile_number || "",
        idNumber: initialData?.id_proof_number || "",
        id_proof_number: initialData?.id_proof_number || "",
        responsibleOrganization: initialData?.organisation || "",
        organisation: initialData?.organisation || "",
        designation: initialData?.designation || "",
        categoryid: String(initialData?.sub_category?.sub_category_id || initialData?.sub_category_type || ""),
        sub_category_type: initialData?.sub_category?.sub_category_id || initialData?.sub_category_type || "",
        sports_id: initialData?.sports_id || initialData?.sport_id || "",
        country: String(initialData?.country_id || initialData?.state_id || initialData?.Country?.id || "12"),
        venue_ids: initialData?.venue_ids || [],
        photo: null,
        officeIDImage: null,
        letter_of_authorization: initialData?.letter_of_authorization || "",
        gender: (initialData?.gender === "Male" || initialData?.gender === "M" || initialData?.gender === "1") ? "1" : (initialData?.gender === "Female" || initialData?.gender === "F" || initialData?.gender === "2") ? "2" : (initialData?.gender === "Other" || initialData?.gender === "OTHERS" || initialData?.gender === "3") ? "3" : "",
        idType: initialData?.id_proof_number ? (initialData?.id_proof_number.length === 12 ? "aadhaarcard" : "passport") : "",
      };
      
      setForm(mapped);
      setOriginalForm(mapped); // Store original data for comparison
      
      // Set existing image URLs
      setExistingPhotoUrl(fixUploadUrl(initialData?.player_image_path || ""));
      setExistingOfficeIdUrl(fixUploadUrl(initialData?.office_id_image || ""));
      setErrors({});
      
      // Set venue IDs if they exist
      if (initialData?.venue_ids) {
        if (Array.isArray(initialData.venue_ids)) {
          setSelectedVenueIds(initialData.venue_ids);
          setSelectedVenueIdsState(initialData.venue_ids);
        } else if (typeof initialData.venue_ids === 'string') {
          try {
            const parsed = JSON.parse(initialData.venue_ids);
            if (Array.isArray(parsed)) {
              setSelectedVenueIds(parsed);
              setSelectedVenueIdsState(parsed);
            }
          } catch (_) {}
        }
      }
      
      // Check if this is a media user type and set showHiddenFields accordingly
      const categoryId = String(mapped.categoryid || mapped.sub_category_type);
      if (categoryId === "61" || categoryId === "62") {
        setShowHiddenFields(true);
        setUserType(categoryId);
        setIsDesignationDropdown(false);
      } else {
        setShowHiddenFields(false);
        setUserType(categoryId);
        setIsDesignationDropdown(true);
      }
      
      // Set letter of authorization if it exists
      if (initialData?.letter_of_authorization) {
        setUploadLetterUploaded(true);
      }
    }
  }, [show, initialData]);


  useEffect(() => {
    const getAllCitiesAndVenues = async () => {
      try {
        setLoading(true);
        const result = await axiosInstance.get("/venue/cities");
        setAllCitiesAndVenues(result.data.data);
      } catch (error) {
        console.error("Error fetching cities and venues:", error);
        swal("Error", "Failed to load venues. Please refresh the page.", "error");
      } finally {
        setLoading(false);
      }
    };
    getAllCitiesAndVenues();
  }, []);

  const getUserTypes = async () => {
    try {
      setLoading(true);
      console.log("Fetching user types...");
      const response = await axiosInstance.get("/user-sub-type/all?page=1&limit=100", {
        timeout: 15000, // 15 second timeout
      });
      const data = response.data?.data || [];
      console.log("User types API response:", data);
      // Map the API response format to the expected format
      const mappedUserTypes = data.map((item) => ({
        id: item.sub_category_id,
        sub_category_id: item.sub_category_id,
        sub_category_name_view: item.sub_category_name_view,
        active_status: item.active_status
      }));
      console.log("Mapped user types:", mappedUserTypes);
      setSubTypes(mappedUserTypes);
    } catch (error) {
      console.error("Error fetching User Types:", error);
      
      let errorMessage = "Failed to load user types. ";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage += "Request timed out. Please refresh the page.";
      } else if (error.response) {
        const status = error.response.status;
        if (status >= 500) {
          errorMessage += "Server error. Please try again later.";
        } else if (status === 404) {
          errorMessage += "User types service not available.";
        } else {
          errorMessage += "Please refresh the page to try again.";
        }
      } else if (error.request) {
        errorMessage += "Network error. Please check your connection.";
      } else {
        errorMessage += "Please refresh the page to try again.";
      }
      
      swal("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };


  const getCountries = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/venue/allCountries", {
        timeout: 15000, // 15 second timeout
      });
      const data = response.data?.data || [];
      // MCD is specific to Delhi only, so keep Delhi only
      const delhiFiltered = data.filter((c) => String(c.id) === "12" || c.name?.toLowerCase() === "delhi");
      const list = delhiFiltered.length > 0 ? delhiFiltered : (data.length > 0 ? data : [{ id: 12, name: "Delhi" }]);
      setCountries(list);
      setForm((prev) => ({
        ...prev,
        country: prev.country || String(list[0]?.id || "12"),
      }));
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountries([{ id: 12, name: "Delhi" }]);
      setForm((prev) => ({
        ...prev,
        country: prev.country || "12",
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await getUserTypes();
      getCountries();
    };
    if (show) loadData();
  }, [show]);

  function isValidMobileNumber(number) {
    const MOBILE_NUMBER_REGEX = /^(?:\+91|0)?[6-9]\d{9}$/;
    return MOBILE_NUMBER_REGEX.test(number);
  }

  function isValidEmail(email) {
    const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
    return EMAIL_REGEX.test(email);
  }

  const handleUploadLetterChange = async (e) => {
    const file = e.target.files[0];

    setUploadLetterError(""); // Clear previous error

    if (!file) {
      setUploadLetterError("Please select a file to upload.");
      return;
    }

    // Validate file type
    const acceptedTypes = [".pdf", ".doc", ".docx"];
    if (!acceptedTypes.some(type => file.name.toLowerCase().endsWith(type))) {
      setUploadLetterError("Invalid file type. Please upload a PDF or Word document.");
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setUploadLetterError("File size exceeds the limit of 5 MB.");
      e.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    formData.append("id", 1);
    
    try {
      setUploading(true);
      const response = await axiosInstance.post("/uploads", formData, {
        timeout: 30000, // 30 second timeout
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      if (response.data && response.data.success) {
        console.log("Upload successful:", response.data.data["1"]);
        const uploadedFileUrl = response.data.data["1"];
        setForm((prev) => ({
          ...prev,
          letter_of_authorization: uploadedFileUrl,
        }));
        setUploadLetterUploaded(true);
        swal("Success", "File uploaded successfully!", "success");
      } else {
        throw new Error(response.data?.message || "Upload failed");
      }
    } catch (error) {
      console.error("File upload error:", error);
      
      let errorMessage = "An unexpected error occurred during upload.";
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = "Upload timeout. Please try again with a smaller file.";
      } else if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            errorMessage = "Invalid file format. Please check your file and try again.";
            break;
          case 413:
            errorMessage = "File too large. Please reduce the file size and try again.";
            break;
          case 415:
            errorMessage = "Unsupported file type. Please upload a PDF or Word document.";
            break;
          case 500:
            errorMessage = "Server error during upload. Please try again later.";
            break;
          default:
            errorMessage = data?.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      setUploadLetterError(errorMessage);
      setUploadLetterUploaded(false);
      e.target.value = ""; // Clear the file input
    } finally {
      setUploading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      // Handle file input changes
      const file = files[0];
      const maxFileSize = 2 * 1024 * 1024; // 2 MB size limit
      const allowedFileTypes = [
        "image/jpeg", 
        "image/jpg", 
        "image/png", 
        "image/webp", 
        "image/gif", 
        "image/bmp", 
        "image/tiff"
      ];

      if (file) {
        if (file.size > maxFileSize) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "File size should not exceed 2 MB.",
          }));
          e.target.value = ""; // Clear file input
        } else if (!allowedFileTypes.includes(file.type)) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: "Only image files (JPEG, PNG, WEBP, GIF, BMP, TIFF) are allowed.",
          }));
          e.target.value = ""; // Clear file input
    } else {
          setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
          setForm((prev) => ({
            ...prev,
            [name]: file,
          }));

          // ✅ Add Image Preview
          URL.createObjectURL(file);
        }
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      }
    } else if (name === "categoryid") {
      const wasMedia = userType === "61" || userType === "62";
      const willBeMedia = value === "61" || value === "62";

      // If leaving non-media, remember current designation
      if (!wasMedia && form.designation) {
        setPreviousNonMediaDesignation(form.designation);
      }

      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));

      setUserType(value);

      if (willBeMedia) {
        setIsDesignationDropdown(false);
        setShowHiddenFields(true);
        setForm((prev) => ({
          ...prev,
          designation: value === "61" ? "Media-Journalist" : "Media-Photo/Video",
        }));
      } else {
        setIsDesignationDropdown(true);
        setShowHiddenFields(false);
        setForm((prev) => ({
          ...prev,
          designation: previousNonMediaDesignation || prev.designation || "",
        }));
      }
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    } else if (name === "designation") {
      if (!(userType === "61" || userType === "62")) {
        setPreviousNonMediaDesignation(value);
      }
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (name === "mobile") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (value && !isValidMobileNumber(value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Please enter a valid mobile number (10 digits starting with 6-9).",
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      }
    } else if (name === "email") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
      if (value && !isValidEmail(value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Please enter a valid email address (e.g., user@example.com).",
        }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      }
    } else if (name === "gender" || name === "sportid") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    } else if (name === "country") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    } else if (name === "idType") {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    } else if (name === "idNumber") {
      // Allow alphanumeric characters and common symbols for ID numbers
      const idNumberRegex = /^[a-zA-Z0-9\s\-_/]*$/;
      if (value && !idNumberRegex.test(value)) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "ID number can only contain letters, numbers, spaces, hyphens, underscores, and forward slashes.",
        }));
      } else {
        setForm((prev) => ({
          ...prev,
          [name]: value,
        }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      }
    } else {
      const isValid = /^[a-zA-Z\s]*$/.test(value);
      if (isValid && value.length <= 50) {
        setForm((prev) => ({
          ...prev,
          [name]: value,
        }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      } else if (value.length > 50) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Input cannot exceed 50 characters.",
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "Only alphabetic characters and spaces are allowed.",
        }));
      }
    }
  };

  const handleOfficeIDFileChange = async (e) => {
    const file = e.target.files[0];
    const maxFileSize = 2 * 1024 * 1024; // 2MB limit
    const allowedFileTypes = [
      "image/jpeg", 
      "image/jpg", 
      "image/png", 
      "image/webp", 
      "image/gif", 
      "image/bmp", 
      "image/tiff"
    ];

    if (file) {
      if (file.size > maxFileSize) {
        setOfficeIDError("File size should not exceed 2 MB.");
        e.target.value = ""; // Reset input field
      } else if (!allowedFileTypes.includes(file.type)) {
        setOfficeIDError("Only image files (JPEG, PNG, WEBP, GIF, BMP, TIFF) are allowed.");
        e.target.value = "";
      } else {
        setOfficeIDError("");
        setUploading(true);
        
        try {
          const formData = new FormData();
          formData.append("image", file);
          formData.append("id", 2);
          
          const response = await axiosInstance.post("/uploads", formData, {
            timeout: 30000,
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          });
          
          if (response.data && response.data.success) {
            const uploadedImageUrl = fixUploadUrl(response.data.data["2"]);
            setForm((prev) => ({
              ...prev,
              officeIDImage: uploadedImageUrl,
            }));
            const imageUrl = URL.createObjectURL(file);
            setOfficeIDImageURL(imageUrl);
            swal("Success", "ID document uploaded successfully!", "success");
          } else {
            throw new Error(response.data?.message || "Upload failed");
          }
        } catch (error) {
          console.error("ID document upload error:", error);
          setOfficeIDError("Failed to upload ID document. Please try again.");
          e.target.value = "";
        } finally {
          setUploading(false);
        }
      }
    } else {
      setOfficeIDError("");
    }
  };

  const handlePhotoFileChange = async (e) => {
    const file = e.target.files[0];
    const maxFileSize = 2 * 1024 * 1024; // 2MB limit
    const allowedFileTypes = [
      "image/jpeg", 
      "image/jpg", 
      "image/png", 
      "image/webp", 
      "image/gif", 
      "image/bmp", 
      "image/tiff"
    ];

    if (file) {
      if (file.size > maxFileSize) {
        setFileError("File size should not exceed 2 MB.");
        e.target.value = ""; // Reset input field
      } else if (!allowedFileTypes.includes(file.type)) {
        setFileError("Only image files (JPEG, PNG, WEBP, GIF, BMP, TIFF) are allowed.");
        e.target.value = "";
      } else {
        setFileError("");
        setUploading(true);

        try {
          const formData = new FormData();
          formData.append("image", file);
          formData.append("id", 3);
          
          const response = await axiosInstance.post("/uploads", formData, {
            timeout: 30000,
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          });
          
          if (response.data && response.data.success) {
            const uploadedImageUrl = fixUploadUrl(response.data.data["3"]);
            setForm((prev) => ({
              ...prev,
              photo: uploadedImageUrl,
            }));
            const imageUrl = URL.createObjectURL(file);
            setImageURL(imageUrl);
            swal("Success", "Profile image uploaded successfully!", "success");
          } else {
            throw new Error(response.data?.message || "Upload failed");
          }
        } catch (error) {
          console.error("Profile image upload error:", error);
          setFileError("Failed to upload profile image. Please try again.");
          e.target.value = "";
        } finally {
          setUploading(false);
        }
      }
    } else {
      setFileError("");
    }
  };

  const validate = () => {
    const next = {};
    if (!form?.registration_data_id && !form?.id) next.registration_data_id = "Missing id";
    if (!form.firstname) next.firstname = "Required";
    if (!form.email) next.email = "Required";
    if (!form.mobile && !form.mobile_number) next.mobile = "Required";
    if (!form.gender) next.gender = "Required";
    if (!form.sub_category_type && !form.categoryid) next.sub_category_type = "Required";
    if (!form.responsibleOrganization && !form.organisation) next.responsibleOrganization = "Required";
    if (!form.designation) next.designation = "Required";
    if (!form.country) next.country = "Required";
    if (!form.idType) next.idType = "Required";
    if (!form.idNumber && !form.id_proof_number) next.idNumber = "Required";
    if (!form.photo && !existingPhotoUrl) next.photo = "Profile image is required";
    if (!form.officeIDImage && !existingOfficeIdUrl) next.officeIDImage = "ID document is required";

    // Validate venue selection for media users
    if (showHiddenFields && selectedVenueIds.length === 0) {
      next.venues = "Please select at least one venue for media personnel.";
    }

    // Validate letter upload for media users
    if (showHiddenFields && !form.letter_of_authorization) {
      next.letter_of_authorization = "Please upload the letter of authorization for media personnel.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => {
    const fd = new FormData();
    
    // Always include the registration ID
    fd.append("registration_data_id", form.registration_data_id || form.id);

    // Helper function to check if a field has changed
    const hasChanged = (fieldName, currentValue, originalValue) => {
      if (fieldName === 'photo' || fieldName === 'officeIDImage') {
        // For files, check if a new file was uploaded
        return currentValue instanceof File || (typeof currentValue === 'string' && currentValue !== originalValue);
      }
      return currentValue !== originalValue;
    };

    // Only include changed fields
    if (hasChanged('firstname', form.firstname, originalForm.firstname)) {
      fd.append("firstname", form.firstname || "");
    }
    
    if (hasChanged('middlename', form.middlename, originalForm.middlename)) {
      fd.append("middlename", form.middlename || "");
    }
    
    if (hasChanged('lastname', form.lastname, originalForm.lastname)) {
      fd.append("lastname", form.lastname || "");
    }
    
    if (hasChanged('email', form.email, originalForm.email)) {
      fd.append("email", form.email || "");
    }
    
    if (hasChanged('gender', form.gender, originalForm.gender)) {
      fd.append("gender", form.gender || "");
    }
    
    if (hasChanged('mobile', form.mobile, originalForm.mobile)) {
      fd.append("mobile_number", form.mobile || "");
    }
    
    if (hasChanged('sportid', form.sportid, originalForm.sportid)) {
      fd.append("sports_id", String(form.sportid || ""));
    }
    
    if (hasChanged('categoryid', form.categoryid, originalForm.categoryid)) {
      fd.append("sub_category_type", String(form.categoryid || ""));
    }
    
    if (hasChanged('responsibleOrganization', form.responsibleOrganization, originalForm.responsibleOrganization)) {
      fd.append("organisation", form.responsibleOrganization || "");
    }
    
    if (hasChanged('designation', form.designation, originalForm.designation)) {
      fd.append("designation", form.designation || "");
    }
    
    if (hasChanged('country', form.country, originalForm.country)) {
      fd.append("country", String(form.country || ""));
    }
    
    if (hasChanged('idType', form.idType, originalForm.idType)) {
      fd.append("idType", form.idType || "");
    }
    
    if (hasChanged('idNumber', form.idNumber, originalForm.idNumber)) {
      fd.append("idNumber", form.idNumber || "");
    }

    // Check if venues have changed
    const originalVenueIds = originalForm.venue_ids || [];
    const venuesChanged = JSON.stringify(selectedVenueIds.sort()) !== JSON.stringify(originalVenueIds.sort());
    if (venuesChanged) {
      selectedVenueIds.forEach((id) => {
        fd.append("venue_ids", id);
      });
    }

    // Files - only include if they have changed (new file uploaded or different URL)
    if (hasChanged('photo', form.photo, originalForm.photo)) {
      if (form.photo instanceof File) {
        fd.append("image", form.photo);
      } else if (typeof form.photo === 'string' && form.photo !== originalForm.photo) {
        fd.append("image", form.photo);
        fd.append("player_image_path", form.photo);
      }
    }
    
    if (hasChanged('officeIDImage', form.officeIDImage, originalForm.officeIDImage)) {
      if (form.officeIDImage instanceof File) {
        fd.append("officeIDImage", form.officeIDImage);
      } else if (typeof form.officeIDImage === 'string' && form.officeIDImage !== originalForm.officeIDImage) {
        fd.append("officeIDImage", form.officeIDImage);
        fd.append("office_id_image", form.officeIDImage);
      }
    }
    
    if (hasChanged('letter_of_authorization', form.letter_of_authorization, originalForm.letter_of_authorization)) {
      if (form.letter_of_authorization) {
        fd.append("letter_of_authorization", form.letter_of_authorization);
      }
    }

    return fd;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validate()) {
      swal("Validation", "Please complete required fields.", "warning");
      return;
    }

    try {
      setSaving(true);
      const payload = buildPayload();
      const res = await enhancedAccreditationService.updateRegistration(payload);
      swal("Success", res.message || "Registration updated successfully", "success");
      onUpdated?.(res.data);
      onClose?.();
    } catch (error) {
      console.error("Error submitting form:", error);
      
      let errorMessage = "An unexpected error occurred. Please try again later.";
      let errorTitle = "Error";

      if (error.code === 'ECONNABORTED') {
        errorTitle = "Timeout Error";
        errorMessage = "The request took too long to complete. Please check your internet connection and try again.";
      } else if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const data = error.response.data;
        
        switch (status) {
          case 400:
            errorTitle = "Bad Request";
            errorMessage = data?.message || "Please check your input and try again.";
            break;
          case 401:
            errorTitle = "Unauthorized";
            errorMessage = "Your session has expired. Please refresh the page and try again.";
            break;
          case 403:
            errorTitle = "Forbidden";
            errorMessage = "You don't have permission to perform this action.";
            break;
          case 404:
            errorTitle = "Not Found";
            errorMessage = "The requested service is not available.";
            break;
          case 413:
            errorTitle = "File Too Large";
            errorMessage = "The uploaded file is too large. Please reduce the file size and try again.";
            break;
          case 422:
            errorTitle = "Validation Error";
            errorMessage = data?.message || "Please check your input and try again.";
            break;
          case 429:
            errorTitle = "Too Many Requests";
            errorMessage = "Too many requests. Please wait a moment and try again.";
            break;
          case 500:
            errorTitle = "Server Error";
            errorMessage = "A server error occurred. Please try again later.";
            break;
          case 502:
          case 503:
          case 504:
            errorTitle = "Service Unavailable";
            errorMessage = "The service is temporarily unavailable. Please try again later.";
            break;
          default:
            errorMessage = data?.message || errorMessage;
        }
      } else if (error.request) {
        // Network error
        errorTitle = "Network Error";
        errorMessage = "Unable to connect to the server. Please check your internet connection and try again.";
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
      }

      swal(errorTitle, errorMessage, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Accreditation</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={saving}></button>
          </div>
          <div className="modal-body">
            {/* Match ManualForm spacing and control sizes */}
            <style>{`
              .form-control { height: 40px !important; }
              .form-group { margin-bottom: 1.5rem !important; }
              .form-group label { margin-bottom: 0.5rem !important; }
            `}</style>
            <div className="mb-4 d-flex justify-content-between align-items-center">
              <h4 className="mb-0">
                <label>Personal Information</label>
              </h4>
              <span className="text-red small">* marked fields are required</span>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Loading Overlay */}
              {loading && (
                <div 
                  className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    zIndex: 1000,
                    top: 0,
                    left: 0,
                    minHeight: '400px'
                  }}
                >
                  <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Loading form data...</p>
                  </div>
                </div>
              )}
              
              <div className="row">
                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              First Name<span className="text-red">*</span>
                            </strong>
                          </label>
                          <input
                            type="text"
                            name="firstname"
                            className="form-control"
                            placeholder="First Name"
                            value={form.firstname}
                            onChange={handleChange}
                            required
                          />
                          {errors.firstname && (
                            <span className="text-danger">
                              {errors.firstname}
                            </span>
                          )}
                  </div>
                </div>
                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>Middle Name</strong>
                          </label>
                          <input
                            type="text"
                            name="middlename"
                            className="form-control"
                            placeholder="Middle Name"
                            value={form.middlename}
                            onChange={handleChange}
                          />
                          {errors.middlename && (
                            <span className="text-danger">
                              {errors.middlename}
                            </span>
                          )}
                  </div>
                </div>
                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>Last Name</strong>
                          </label>
                          <input
                            type="text"
                            name="lastname"
                            className="form-control"
                            placeholder="Last Name"
                            value={form.lastname}
                            onChange={handleChange}
                          />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                          <strong>
                            Gender<span className="text-red">*</span>
                          </strong>
                          </label>
                          <select
                            name="gender"
                            className="form-control"
                            value={form.gender}
                            onChange={handleChange}
                            required
                          >
                      <option value="">Select Gender</option>
                      <option value="1">Male</option>
                      <option value="2">Female</option>
                      <option value="3">Other</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              Email<span className="text-red">*</span>
                            </strong>
                          </label>
                          <input
                            type="email"
                            name="email"
                            className="form-control"
                            placeholder="Email"
                            value={form.email || ""}
                            onChange={(e) => handleChange(e)}
                            required
                          />
                          {errors.email && (
                            <div className="text-danger mt-1">
                              {errors.email}
                            </div>
                          )}
                  </div>
                </div>
                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              Mobile Number<span className="text-red">*</span>
                            </strong>
                          </label>
                          <input
                            type="text"
                            name="mobile"
                            className="form-control"
                            placeholder="Mobile Number"
                            value={form.mobile || ""}
                            onChange={(e) => handleChange(e)}
                            required
                          />
                          {errors.mobile && (
                            <div className="text-danger mt-1">
                              {errors.mobile}
                            </div>
                          )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                          <strong>
                              Select User Type<span className="text-red">*</span>
                          </strong>
                          </label>
                    <SearchableUserTypeDropdown
                            userTypes={subtypes.filter((type) =>
                              isMedia
                                ? type.sub_category_id === 61 ||
                                  type.sub_category_id === 62
                                : true
                            )}
                            value={form.categoryid || ''}
                            onChange={(value) => {
                              const event = {
                                target: {
                                  name: "categoryid",
                                  value: value
                                }
                              };
                              handleChange(event);
                            }}
                      placeholder="Select User Type"
                      required={true}
                      className="form-control"
                      filterActiveOnly={true}
                      useStandardFormControl={true}
                    />
                </div>
              </div>

                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              Upload Profile Image<span className="text-red">*</span>
                              <i
                                className="fa fa-info-circle ms-2 text-muted"
                                title="Upload a clear profile photo. Supported formats: JPEG, PNG, WEBP, GIF, BMP, TIFF. Max size: 2MB. Square aspect ratio recommended."
                                style={{ cursor: "pointer" }}
                              ></i>
                            </strong>
                          </label>
                          <div className="d-flex gap-2 align-items-center">
                    {existingPhotoUrl && !form.photo && (
                      <button
                        type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => { 
                                  setSelectedImage(existingPhotoUrl); 
                                  setSelectedImageAlt('Profile Image'); 
                                  setShowImageModal(true); 
                                }}
                              >
                                <i className="fa fa-eye me-1"></i>Preview Current Image
                      </button>
                    )}
                            <div className="flex-grow-1">
                    <ImageUploadWithCrop
                      name="photo"
                      label=""
                                // required={true}
                                onChange={handlePhotoFileChange}
                                error={fileError || errors.photo}
                                onPreviewClick={(imageUrl, imageAlt) => {
                                  setSelectedImage(imageUrl);
                                  setSelectedImageAlt(imageAlt);
                                  setShowImageModal(true);
                                }}
                                placeholder={existingPhotoUrl ? `Current: ${existingPhotoUrl.split('/').pop()}` : "Choose Profile Image"}
                                maxSize={2 * 1024 * 1024} // 2MB
                                acceptedTypes={[
                                  "image/jpeg", 
                                  "image/jpg", 
                                  "image/png", 
                                  "image/webp", 
                                  "image/gif", 
                                  "image/bmp", 
                                  "image/tiff"
                                ]}
                                aspectRatio={1} // Square aspect ratio for profile images
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* </div> */}

                      <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              Responsible Organization<span className="text-red">*</span>
                            </strong>
                          </label>
                          <input
                            type="text"
                            name="responsibleOrganization"
                            className="form-control"
                            placeholder="Responsible Organization"
                            value={form.responsibleOrganization}
                      onChange={handleChange}
                            required
                    />
                  </div>
                </div>

                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              Designation<span className="text-red">*</span>
                            </strong>
                          </label>

                          {userType === "61" || userType === "62" ? (
                            <input
                              type="text"
                              name="designation"
                              className="form-control"
                              value={form.designation}
                              readOnly
                              required
                            />
                          ) : (
                            <input
                              type="text"
                              name="designation"
                              className="form-control"
                              placeholder="Enter Designation"
                              value={form.designation}
                              onChange={handleChange}
                              required
                            />
                          )}
                  </div>
                </div>

                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              State<span className="text-red">*</span>
                            </strong>
                          </label>
                          <select
                            name="country"
                            className="form-control"
                            value={form.country || "12"}
                            onChange={handleChange}
                            required
                          >
                            {countries.length > 1 && <option value="">Select State</option>}
                            {countries.map((country) => (
                              <option key={country.id} value={country.id}>
                                {country.name}
                              </option>
                            ))}
                          </select>
                          {errors.country && (
                            <div className="text-danger mt-1">
                              {errors.country}
                  </div>
                          )}
                </div>
              </div>

                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              ID Type<span className="text-red">*</span>
                            </strong>
                          </label>
                          <select
                            name="idType"
                            className="form-control"
                            value={form.idType}
                            onChange={handleChange}
                            required
                          >
                            <option value="">Select ID Type</option>
                            <option value="passport">Passport</option>
                            <option value="aadhaarcard">Aadhaar</option>
                          </select>
                          {errors.idType && (
                            <div className="text-danger mt-1">
                              {errors.idType}
                            </div>
                          )}
                        </div>
                      </div>

                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              ID Number<span className="text-red">*</span>
                            </strong>
                          </label>
                          <input
                            type="text"
                            name="idNumber"
                            className="form-control"
                            placeholder="Enter ID Number"
                            value={form.idNumber}
                      onChange={handleChange}
                            required
                          />
                          {errors.idNumber && (
                            <div className="text-danger mt-1">
                              {errors.idNumber}
                            </div>
                          )}
                  </div>
                </div>

                <div className="col-md-4">
                        <div className="form-group mb-4">
                          <label className="mb-2">
                            <strong>
                              Upload ID Document<span className="text-red">*</span>
                            <i
                              className="fa fa-info-circle ms-2 text-muted"
                                title="Upload your ID document. For media personnel: Press card. For others: Office ID or Aadhar card. Supported formats: JPEG, PNG, WEBP, GIF, BMP, TIFF. Max size: 2MB. ID card aspect ratio recommended."
                              style={{ cursor: "pointer" }}
                            ></i>
                            </strong>
                          </label>
                          <div className="d-flex gap-2 align-items-center">
                    {existingOfficeIdUrl && !form.officeIDImage && (
                      <button
                        type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => { 
                                  setSelectedImage(existingOfficeIdUrl); 
                                  setSelectedImageAlt('ID Document'); 
                                  setShowImageModal(true); 
                                }}
                              >
                                <i className="fa fa-eye me-1"></i>Preview Current ID
                      </button>
                    )}
                            <div className="flex-grow-1">
                    <ImageUploadWithCrop
                      name="officeIDImage"
                      label=""
                                // required={true}
                                onChange={handleOfficeIDFileChange}
                                error={officeIDError || errors.officeIDImage}
                                onPreviewClick={(imageUrl, imageAlt) => {
                                  setSelectedImage(imageUrl);
                                  setSelectedImageAlt(imageAlt);
                                  setShowImageModal(true);
                                }}
                                placeholder={existingOfficeIdUrl ? `Current: ${existingOfficeIdUrl.split('/').pop()}` : "Choose ID Document"}
                                maxSize={2 * 1024 * 1024} // 2MB
                                acceptedTypes={[
                                  "image/jpeg", 
                                  "image/jpg", 
                                  "image/png", 
                                  "image/webp", 
                                  "image/gif", 
                                  "image/bmp", 
                                  "image/tiff"
                                ]}
                                aspectRatio={1.4} // Common ID card aspect ratio
                    />
                  </div>
                </div>
                  </div>
                </div>
                  </div>

                      {showHiddenFields && (
                        <>
                          {/* Media Personnel Section Divider */}
                          <div className="col-12">
                            <hr className="my-4" />
                            <div className="mb-4 d-flex justify-content-between align-items-center">
                              <h5 className="mb-0 text-primary">
                                <i className="fa fa-camera me-2"></i>
                                Media Personnel Information
                              </h5>
                </div>
                  </div>
                          

                          {/* Upload Letter Section */}
                          <div className="col-md-12 mb-4">
                          </div>
                        </>
                      )}

                    <div className="row mt-5">
                      <div className="col-12">
                        <div className="d-flex justify-content-center align-items-center py-4">
                      <button
                        className="btn btn-primary d-flex align-items-center justify-content-center"
                        type="submit"
                        style={{
                          width: "285px",
                          height: "42px",
                          top: "930px",
                          left: "577px",
                          opacity: "1",
                        }}
                        disabled={saving} // Disable button while loading
                      >
                        {saving ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </button>
                        </div>
                      </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <ImageModal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        imageUrl={selectedImage}
        imageAlt={selectedImageAlt}
      />
    </div>
  );
};

export default EditAccreditationModal;